/**
 * Runtime shell — compatibility surface for the platform.
 *
 * The presentational shell (top bar / user menu / account + admin panels)
 * moved into the shared package (`@twasta/page-runtime/shell`) so the
 * standalone runtime client renders the SAME end-user top bar as the Run
 * view. This module re-exports the shared pieces and keeps the
 * platform-only adapters (site-user identity + ShellApi bound to
 * `@/api/website`, and the react-hot-toast notifier the shared panels use).
 */
import toast from 'react-hot-toast'
export {
  RuntimeTopBar, EndUserTopBar, UserMenu, AccountModal, AdminConsole,
  ShellModal, shellInput, shellBtn, extractError,
} from '@twasta/page-runtime/shell'
export type {
  EndUserTopBarProps, UserMenuItem, AdminExtraTab,
  ShellApi, ShellApp, ShellCapabilities, ShellGrant, ShellIdentity, ShellNotify,
  ShellObject, ShellObjectAction, ShellRepositoryTargets, ShellRestriction, ShellRole, ShellUser,
} from '@twasta/page-runtime/shell'

import {
  siteChangePassword, siteLogout,
  siteAdminListUsers, siteAdminDeleteUser, siteAdminSetRoles,
  siteAdminSecurityContext, siteAdminSetContextRoles,
  siteAdminSetUserAdmin, siteAdminSetUserActive, siteAdminSendReset, siteAdminUnlock,
  siteAdminResetMfa, siteMfaStatus, siteMfaTotpStart, siteMfaTotpConfirm, siteMfaRecoveryCodes,
  siteMfaDisable, siteAdminGetSecurity, siteAdminPutSecurity,
  siteAdminListGrants, siteAdminSetGrant, siteAdminDeleteGrant,
  siteAdminListRoles, siteAdminCreateRole, siteAdminUpdateRole, siteAdminDeleteRole,
  siteAdminListObjects, projectUserInvite,
  type SiteUser,
} from '@/api/website'
import { listAcl, listTypes, makeDmsClient, saveAcl } from '@/api/dms'
import type { ShellApi, ShellIdentity, ShellNotify, ShellRestriction } from '@twasta/page-runtime/shell'

/** Platform toast adapter for the shared shell panels (react-hot-toast). */
export const platformNotify: ShellNotify = {
  success: (m) => { toast.success(m) },
  error: (m) => { toast.error(m) },
}

/** Map a SiteUser (whoami/login payload) to the surface-agnostic identity. */
export function siteIdentity(u: SiteUser | null): ShellIdentity | null {
  if (!u) return null
  return {
    username: u.username,
    email: u.email,
    isAdmin: u.is_admin || u.roles.includes('admin'),
    roles: u.roles,
  }
}

/**
 * ShellApi bound to a project's site-user endpoints. `onLogout` lets the host
 * clear its local token/session state after the server call.
 */
export function makeSiteShellApi(userId: string, project: string, onLogout?: () => void): ShellApi {
  return {
    changePassword: async (oldPw, newPw) => { await siteChangePassword(userId, project, oldPw, newPw) },
    // My account → two-factor sign-in (the shared TwoFactorPanel).
    mfa: {
      status: async () => (await siteMfaStatus(userId, project)).data,
      totpStart: async (confirm) => (await siteMfaTotpStart(userId, project, confirm)).data,
      totpConfirm: async (code) => (await siteMfaTotpConfirm(userId, project, code)).data,
      recoveryCodes: async (pw, code) => (await siteMfaRecoveryCodes(userId, project, pw, code)).data,
      disable: async (pw, code) => (await siteMfaDisable(userId, project, pw, code)).data,
    },
    logout: async () => { await siteLogout(userId, project); onLogout?.() },
    listUsers: async () => (await siteAdminListUsers(userId, project)).data.users,
    // Invite, never "create with a password": the invitee sets their own on
    // accept, so no administrator handles somebody else's credential. An email
    // that already has an account here is added to this app directly.
    inviteUser: async (b) => {
      const res = await projectUserInvite(
        userId, project, b.email, b.roles || [], !!b.is_admin)
      return { mode: res.data.mode, email_sent: res.data.email_sent }
    },
    deleteUser: async (u) => { await siteAdminDeleteUser(userId, project, u) },
    setRoles: async (u, roles) => { await siteAdminSetRoles(userId, project, u, roles) },
    // Context-based security: roles held inside one value of the app's
    // security context (e.g. one financial entity).
    securityContext: async () => (await siteAdminSecurityContext(userId, project)).data.security_context,
    setContextRoles: async (u, cv, roles) => {
      await siteAdminSetContextRoles(userId, project, u, cv, roles)
    },
    setUserAdmin: async (u, isAdmin) => { await siteAdminSetUserAdmin(userId, project, u, isAdmin) },
    setUserActive: async (u, isActive) => { await siteAdminSetUserActive(userId, project, u, isActive) },
    sendPasswordReset: async (u) => {
      const res = await siteAdminSendReset(userId, project, u)
      return { email_sent: res.data.email_sent }
    },
    unlockUser: async (u) => { await siteAdminUnlock(userId, project, u) },
    resetUserMfa: async (u) => { await siteAdminResetMfa(userId, project, u) },
    getSignInPolicy: async () => (await siteAdminGetSecurity(userId, project)).data,
    setSignInPolicy: async (mfa) => (await siteAdminPutSecurity(userId, project, mfa)).data,
    listRoles: async () => (await siteAdminListRoles(userId, project)).data.roles,
    createRole: async (name, description) => { await siteAdminCreateRole(userId, project, name, description) },
    updateRole: async (name, description) => { await siteAdminUpdateRole(userId, project, name, description) },
    deleteRole: async (name) => { await siteAdminDeleteRole(userId, project, name) },
    listGrants: async () => (await siteAdminListGrants(userId, project)).data.grants,
    setGrant: async (role, obj, actions) => { await siteAdminSetGrant(userId, project, role, obj, actions) },
    deleteGrant: async (role, obj) => { await siteAdminDeleteGrant(userId, project, role, obj) },
    listObjects: async () => (await siteAdminListObjects(userId, project)).data.objects,
    // Restrictions inside a document repository (business area / folder /
    // kind of document / one document) — the DMS's own rows, edited from the
    // same Access screen as the repository grant.
    listRepositoryRestrictions: async (repo) => {
      const res = await listAcl(project, repo.obj_name)
      return (res.acl || []).map(aclRowToRestriction)
    },
    saveRepositoryRestriction: async (repo, row) => {
      await saveAcl(project, repo.obj_name, {
        scope: row.scope, scope_id: row.scope_id,
        principal_kind: row.principal_kind, principal_id: row.principal_id, rights: row.rights,
      })
    },
    repositoryTargets: async (repo) => {
      const [types, folders] = await Promise.all([
        listTypes(project, repo.obj_name).then((r) => r.types || []),
        makeDmsClient(project, repo.obj_name).folders(),
      ])
      return {
        doctypes: types.map((t) => ({ id: t.id, title: t.title || t.type_key })),
        folders: folders.map((f) => ({ id: f.id, name: f.name, path: f.path })),
      }
    },
  }
}

/** A DMS access-list row (`can_<right>` flags) as the console's shape. */
export function aclRowToRestriction(row: Record<string, unknown>): ShellRestriction {
  const rights: Record<string, boolean> = {}
  for (const [k, v] of Object.entries(row)) {
    if (k.startsWith('can_')) rights[k.slice(4)] = v === 1 || v === true || v === '1'
  }
  return {
    scope: String(row.scope || ''),
    scope_id: row.scope_id == null ? '' : String(row.scope_id),
    principal_kind: String(row.principal_kind || 'role'),
    principal_id: String(row.principal_id || ''),
    rights,
  }
}
